/**
 * 问卷配置类型。
 * 注：原 types.ts 未包含在 source map 里（纯类型文件不产码），本文件按还原代码的用法重建；
 * 字段与后端 validate.mjs 的校验规则对齐，并留有索引签名以兼容未知扩展键。
 */

export type QuestionType = 'text' | 'longtext' | 'single' | 'multi' | 'slider' | 'select' | 'matrix' | 'file';

export type ConditionOp = 'equals' | 'contains' | 'gt' | 'lt' | 'between';

export type Condition = { question: string; op: ConditionOp; value: any };

export type DisplayLogic = { operator: 'AND' | 'OR'; conditions: Condition[] };

export type Option = {
  value: string;
  quota?: number;
  onQuotaFull?: 'jumpNext' | 'end';
  /** 评分题：该选项分值 */
  score?: number;
  [k: string]: any;
};

export type Question = {
  id: string;
  type: QuestionType;
  title: string;
  description?: string;
  /** 描述配图（前端同学版本的题图字段；渲染时与 image 互为兜底） */
  descriptionImage?: string;
  required: boolean;
  requiredRead?: boolean;
  /** 题干配图（data URL） */
  image?: string;
  /** 在前置页显示（随协议页一起填写，如基本信息） */
  onPreface?: boolean;
  options?: Option[];
  params?: { min?: number; max?: number; step?: number; unit?: string; maxLen?: number; rows?: string[]; columns?: string[]; columnScores?: (number | null)[]; allowImageUpload?: boolean };
  displayLogic: DisplayLogic;
  [k: string]: any;
};

/** 前置页独立填写项（仅 text / longtext，随协议页一起填写） */
export type PrefaceQuestion = {
  id: string;
  type: 'text' | 'longtext';
  title: string;
  required: boolean;
  params?: { maxLen?: number; allowImageUpload?: boolean };
  [k: string]: any;
};

/** 文本题答案配图：答案为 { text, images } 对象 */
export type TextAnswer = { text: string; images: string[] };

export const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;
export const MAX_ANSWER_IMAGES = 3;

export function textAnswerText(value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value) && typeof (value as TextAnswer).text === 'string') {
    return (value as TextAnswer).text;
  }
  return typeof value === 'string' ? value : '';
}

export function textAnswerImages(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const images = (value as Partial<TextAnswer>).images;
  return Array.isArray(images) ? images.filter((image): image is string => typeof image === 'string') : [];
}

export function withTextAnswer(text: string, images: string[]): TextAnswer {
  return { text, images };
}

export type SurveySettings = {
  stopCollect?: boolean;
  anonymous?: boolean;
  voiceInput?: boolean;
  whoCanFill?: string;
  loginRequired?: boolean;
  inviteToken?: string;
  /** 每人限填一次（按浏览器设备识别） */
  oncePerUser?: boolean;
  preface?: { title?: string; body?: string; image?: string; requireCheck?: boolean; questions?: PrefaceQuestion[] };
  signature?: { enabled?: boolean; mode?: 'draw' | 'upload'; required?: boolean; onPreface?: boolean };
  feishuTable?: { appToken?: string; tableId?: string; url?: string; fieldNames?: string[] };
  editAnswer?: { enabled?: boolean; tokenTtlHours?: number };
  timeLimit?: { enabled?: boolean; start?: string; end?: string };
  quotaEnabled?: boolean;
  quota?: number;
  submitLimitEnabled?: boolean;
  submitLimit?: number;
  resultPage?: { title?: string; body?: string };
  redirectUrl?: { enabled?: boolean; url?: string };
  feishu?: { enabled?: boolean; webhookUrl?: string; bearerToken?: string; publicBaseUrl?: string };
  [k: string]: any;
};

export type SurveyConfig = {
  id: string;
  header: { mainTitle: string; subTitle?: string };
  questions: Question[];
  settings: SurveySettings;
  [k: string]: any;
};
